import { readXsrfToken } from './csrf';

export interface UserSummary {
  id: string;
  email: string;
  fullName: string;
  role: string;
  teamId: string | null;
  allowedPages: string[];
  allowedTools: string[];
  /**
   * HR ▸ Appraisals access tier, computed server-side by AppraisalAccess:
   *   'full' (Management / HR-manager / admin) · 'team' (a team manager) ·
   *   'self' (regular employee, own form only) · 'none' (viewer / agent).
   * Optional for forward-compat with cached sessions issued before the
   * field existed; treat a missing value as 'none'.
   */
  appraisalsAccess?: string;
  /**
   * Data ▸ Tariff — true when the user may use the Data/Tariff module, via
   * the tariff_editor/manager/admin role OR the team-based grant (a member of
   * a configured team, e.g. the Data team). Mirrors the server IsTariffEditor;
   * used by `canSeeTile('data')`. Optional for forward-compat with sessions
   * cached before the field existed — treat a missing value as false.
   */
  canEditTariff?: boolean;
  /**
   * Logs ▸ modification auto-approval (`users.auto_approve`). Mirrors the
   * server rule in `LogsEditService.IsAutoApproveUser` (admin OR the grant).
   * Optional for forward-compat with sessions cached before the field
   * existed — treat a missing value as false.
   */
  autoApprove?: boolean;
}

export interface LoginResponse {
  token: string | null; // null in cookie mode
  expiresAt: string | null;
  user: UserSummary;
}

interface ApiResult<T> {
  ok: boolean;
  data?: T;
  error?: string;
}

export class ApiError extends Error {
  public readonly requestId?: string;

  constructor(public status: number, message: string, requestId?: string) {
    super(message);
    this.name = 'ApiError';
    this.requestId = requestId;
  }
}

/**
 * Lowercase header name on purpose — `Headers.get` is case-insensitive,
 * but keeping the canonical kebab-case literal documents the wire format
 * (matches `RequestIdMiddleware.HeaderName` on the server).
 */
function readRequestId(res: Response): string | undefined {
  return res.headers.get('x-request-id') ?? undefined;
}

const BASE = '/api';

/**
 * Set by the API on a 400 whose antiforgery cookie pair it just re-minted
 * (see AntiforgeryTokenIssuer / the antiforgery middleware in Program.cs).
 * Lowercase because `Headers.get` is case-insensitive and the wire form is
 * kebab-case.
 */
const XSRF_REFRESH_HEADER = 'x-antiforgery-refresh';

/**
 * One fetch, plus a single silent replay when the API answers a mutation with
 * "your antiforgery token was bound to a different identity, here's a fresh
 * one". The Set-Cookie on that 400 has already landed by the time the promise
 * resolves, so the replay re-reads `XSRF-TOKEN` and goes out with the good
 * token. Without this, an identity change between mint and use (login in a
 * second tab, a session whose pair predates the claim-binding fix) surfaces
 * to the user as a dead "Antiforgery token missing or invalid."
 */
async function send(
  path: string,
  init: RequestInit,
  opts: { mutation?: boolean },
): Promise<Response> {
  const attempt = () => {
    const headers = new Headers(init.headers);
    // Only a STRING body may be assumed to be JSON.
    //
    // This used to fire for any non-null body, which silently broke every
    // multipart upload that went through this helper: a `FormData` body is
    // the browser's cue to write `multipart/form-data; boundary=…`, and it
    // only does that when the header is ABSENT. Setting it here replaced
    // that with `application/json`, boundary and all, so the server could
    // not parse the parts and saw no file at all. FormData, Blob, File,
    // URLSearchParams and ArrayBuffer all carry their own correct type —
    // leave every one of them to the browser.
    if (!headers.has('Content-Type') && typeof init.body === 'string') {
      headers.set('Content-Type', 'application/json');
    }
    if (opts.mutation) {
      const xsrf = readXsrfToken();
      if (xsrf) headers.set('X-XSRF-TOKEN', xsrf);
    }
    return fetch(`${BASE}${path}`, { ...init, headers, credentials: 'include' });
  };

  const res = await attempt();
  if (res.status === 400 && opts.mutation && res.headers.get(XSRF_REFRESH_HEADER)) {
    return attempt();
  }
  return res;
}

/**
 * The raw `Response`, with the same cookie, XSRF and antiforgery-replay
 * semantics as `apiFetchRaw`, for a caller that reads the body itself (a
 * line stream: Create with AI's progress, `shared/modules/aiDraft.ts`).
 * The caller checks `res.ok`.
 */
export function apiFetchResponse(path: string, init: RequestInit = {}, opts: { mutation?: boolean } = {}): Promise<Response> {
  return send(path, init, opts);
}

/**
 * Module-scoped API helpers (e.g. `features/coportal/cash-claims/cash-claims.api.ts`)
 * call `apiFetch` instead of writing a parallel `fetch` wrapper. It carries
 * the same `credentials: 'include'` + `X-XSRF-TOKEN` semantics, unwraps the
 * `{ ok, data, error }` envelope, and throws an `ApiError` on any non-ok
 * status or `ok:false` body.
 */
export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
  opts: { mutation?: boolean } = {},
): Promise<T> {
  return request<T>(path, init, opts);
}

/**
 * Raw counterpart to `apiFetch`. Same cookie + XSRF semantics, throws
 * `ApiError` on any non-2xx, but does NOT unwrap the `{ ok, data }`
 * envelope — returns the raw response body so callers can read sibling
 * fields (`{ ok, notes }`, `{ ok, kpis, monthly, ... }`, etc).
 *
 * Promoted from per-module clones (coportal/aiaiai/lemax) so the same
 * defect class (bare `Error`, missing `opts.mutation`, inline XSRF
 * read) cannot regress slice-by-slice — caught three iters running
 * (B27 / B30 / B33). Modules now `import { apiFetchRaw } from '@/auth/api'`.
 */
export async function apiFetchRaw<T>(
  path: string,
  init: RequestInit = {},
  opts: { mutation?: boolean } = {},
): Promise<T> {
  const res = await send(path, init, opts);

  if (!res.ok) {
    let msg = `Request failed with status ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string } | null;
      if (body?.error) msg = body.error;
    } catch {
      /* non-JSON error body — keep default */
    }
    throw new ApiError(res.status, msg, readRequestId(res));
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  opts: { mutation?: boolean } = {},
): Promise<T> {
  const res = await send(path, init, opts);

  // 401 on /auth/me is a normal "not logged in" signal, not an exception case.
  if (res.status === 204) return undefined as T;

  let body: unknown = null;
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) {
    body = await res.json().catch(() => null);
  }

  if (!res.ok) {
    const message =
      (body as ApiResult<unknown> | null)?.error ??
      `Request failed with status ${res.status}`;
    throw new ApiError(res.status, message, readRequestId(res));
  }

  const wrapped = body as ApiResult<T> | null;
  if (wrapped && typeof wrapped === 'object' && 'ok' in wrapped) {
    if (!wrapped.ok) {
      throw new ApiError(res.status, wrapped.error ?? 'Unknown error', readRequestId(res));
    }
    return wrapped.data as T;
  }
  return body as T;
}

export interface AuthedUserSummary {
  user: UserSummary;
  expiresAt: string | null;
}

/**
 * A sign-in that never answers (a dropped connection, a stalled proxy) used
 * to leave the button on "signing in." until the user reloaded. Give up after
 * SIGN_IN_TIMEOUT_MS and say so, so the form can be tried again.
 */
const SIGN_IN_TIMEOUT_MS = 20_000;

async function withSignInTimeout<T>(run: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), SIGN_IN_TIMEOUT_MS);
  try {
    return await run(ctrl.signal);
  } catch (err) {
    if (ctrl.signal.aborted) {
      throw new ApiError(0, 'The server took too long to answer. Check your connection and try again.');
    }
    if (err instanceof TypeError) {
      throw new ApiError(0, 'Could not reach the server. Check your connection and try again.');
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

export const authApi = {
  async login(email: string, password: string): Promise<LoginResponse> {
    return withSignInTimeout((signal) =>
      request<LoginResponse>(
        '/auth/login',
        {
          method: 'POST',
          body: JSON.stringify({ email, password }),
          signal,
        },
        // Login uses [IgnoreAntiforgeryToken]; no XSRF header needed and
        // the cookie does not exist yet on first call.
        { mutation: false },
      ),
    );
  },

  /**
   * Agent Portal sign-in (/agent) — by username, as PHP's agentportal form
   * did (agents have no mailbox). Same cookie session as the staff login;
   * the server then confines the token to the Agent Portal endpoints.
   */
  async agentLogin(username: string, password: string): Promise<LoginResponse> {
    return withSignInTimeout((signal) =>
      request<LoginResponse>(
        '/auth/agent/login',
        {
          method: 'POST',
          body: JSON.stringify({ username, password }),
          signal,
        },
        { mutation: false },
      ),
    );
  },

  async logout(): Promise<void> {
    await request<void>(
      '/auth/logout',
      { method: 'POST' },
      { mutation: true },
    );
  },

  async me(): Promise<AuthedUserSummary | null> {
    try {
      return await request<AuthedUserSummary>('/auth/me');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return null;
      throw err;
    }
  },

  async refresh(): Promise<LoginResponse> {
    return request<LoginResponse>(
      '/auth/refresh',
      { method: 'POST' },
      { mutation: true },
    );
  },
};
